import * as React from 'react';
import uuid from 'uuid-random';
import Box from '@mui/material/Box';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import Chip from '@mui/material/Chip';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
};

const SelectChip = ({ options, name, onChange, multiple, selected: selectedProps, onDelete }) => {
  const [id] = React.useState(uuid());
  const [chosen, setChosen] = React.useState(() => {
    if (selectedProps && multiple) {
      return selectedProps
        .filter(Boolean)
        .map((id) => options.find(({ _id, code }) => [_id, code].includes(id)));
    }

    if (selectedProps && !multiple) {
      return selectedProps;
    }

    return multiple ? [] : '';
  });
  const [open, setOpen] = React.useState(false);

  const handleClose = () => setOpen(false);

  const handleOpen = (e) => {
    if (e?.type === 'mousedown' && ['svg', 'path'].includes(e.target.tagName)) {
      return;
    }

    setOpen(true);
  };

  const handleChange = React.useCallback(
    (event) => {
      const {
        target: { value }
      } = event;

      setChosen(typeof value === 'string' ? value.split(',') : value);

      if (!multiple) setOpen(false);

      if (onChange) onChange(multiple ? value.map(({ _id }) => _id) : [value?._id], value);
    },
    [onChange, multiple]
  );

  const handleDelete = React.useCallback(
    ({ _id, code }) => {
      const value = multiple ? chosen.filter((item) => ![item._id, item.code].includes(_id)) : '';

      setChosen(value);

      handleChange({ target: { value } });

      onDelete && onDelete(code);
    },
    [onDelete, multiple, chosen, handleChange]
  );

  const renderValue = React.useCallback(
    (selected) => {
      if (multiple) {
        return (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {(selected || []).filter(Boolean).map(({ _id }) => {
              const option = options.find((opt) => opt._id === _id);
              return option ? (
                <Chip
                  key={option._id}
                  label={option?.value || option?.name || option?.title}
                  onDelete={() => handleDelete(option)}
                />
              ) : null;
            })}
          </Box>
        );
      }

      return selected?.value || selected?.name || selected?.title;
    },
    [options, multiple, handleDelete]
  );

  return (
    <div>
      <FormControl sx={{ mb: 2, width: 350 }}>
        <InputLabel id={id}>{name}</InputLabel>
        <Select
          open={open}
          labelId={id}
          id={id}
          multiple={multiple}
          value={chosen}
          onClose={handleClose}
          onOpen={handleOpen}
          onChange={handleChange}
          onMouseDown={handleOpen}
          input={<OutlinedInput label="Chip" />}
          inputProps={{ 'aria-label': name }}
          renderValue={renderValue}
          MenuProps={MenuProps}>
          {!multiple && (
            <MenuItem value="">
              <em>-</em>
            </MenuItem>
          )}
          {options.map((option) => {
            const title = option?.value || option?.name || option?.title;

            return (
              <MenuItem key={option._id} value={option}>
                {title}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </div>
  );
};

export default SelectChip;
